import React, { useState, useEffect,useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import {QRCodeCanvas} from 'qrcode.react';
import { 
  FaPlus, 
  FaDownload, 
  FaSearch, 
  FaFilter, 
  FaEdit, 
  FaTrash, 
  FaEye,
  FaGlobe,
  FaRandom,
  FaQrcode
} from 'react-icons/fa';

const ScripturePromises = () => {
  const { isDarkMode } = useTheme();
  const [promises, setPromises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPromise, setEditingPromise] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    scripture_text: '',
    reference: '',
    book: '',
    chapter: '',
    verse_start: '',
    verse_end: '',
    category: 'general'
  });
  
  const [onlineReference, setOnlineReference] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    loadPromises();
    loadCategories();
    generateQRCode();
  }, [pagination.page, searchTerm, categoryFilter, statusFilter]);

  const loadPromises = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(statusFilter && { is_active: statusFilter })
      };

      const response = await axios.get('/api/promises', { params });
      setPromises(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading promises:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load scriptures'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get('/api/promises/categories/list');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const generateQRCode = () => {
    try {
      const inspirationUrl = `${window.location.origin}/inspiration`;
      setQrCodeUrl(inspirationUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (editingPromise) {
        await axios.put(`/api/promises/${editingPromise.id}`, formData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Scripture updated successfully'
        });
      } else {
        await axios.post('/api/promises', formData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Scripture added successfully'
        });
      }
      
      setShowAddForm(false);
      setEditingPromise(null);
      setFormData({
        title: '',
        scripture_text: '',
        reference: '',
        book: '',
        chapter: '',
        verse_start: '',
        verse_end: '',
        category: 'general'
      });
      loadPromises();
    } catch (error) {
      console.error('Error saving promise:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save scripture'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promise) => {
    setEditingPromise(promise);
    setFormData({
      title: promise.title,
      scripture_text: promise.scripture_text,
      reference: promise.reference,
      book: promise.book,
      chapter: promise.chapter.toString(),
      verse_start: promise.verse_start.toString(),
      verse_end: promise.verse_end ? promise.verse_end.toString() : '',
      category: promise.category
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/promises/${id}`);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Scripture deleted successfully'
        });
        loadPromises();
      } catch (error) {
        console.error('Error deleting promise:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete scripture'
        });
      }
    }
  };

  const fetchOnlineScripture = async () => {
    if (!onlineReference.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Warning',
        text: 'Please enter a scripture reference'
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/promises/fetch-online', {
        reference: onlineReference
      });

      const data = response.data.data;
      setFormData({
        title: data.title,
        scripture_text: data.scripture_text,
        reference: data.reference,
        book: data.book,
        chapter: data.chapter.toString(),
        verse_start: data.verse_start.toString(),
        verse_end: data.verse_end ? data.verse_end.toString() : '',
        category: 'general'
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Scripture fetched successfully'
      });
    } catch (error) {
      console.error('Error fetching online scripture:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch scripture from online source'
      });
    } finally {
      setLoading(false);
    }
  };

  // QR download support
  const qrCanvasRef = useRef(null);
  const downloadQRCode = () => {
    try {
      // Get the canvas from within the ref wrapper
      const canvas = qrCanvasRef.current?.querySelector('canvas');
      
      if (!canvas) {
        console.error('Canvas not found');
        return;
      }
      
      // Create download link WITHOUT modifying canvas size
      const link = document.createElement('a');
      link.download = 'rccg-inspiration-qr.png';
      link.href = canvas.toDataURL('image/png');
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('QR download failed:', error);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Unable to download QR code. Please try again.'
      });
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStatusFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className={`min-h-screen p-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Scripture Promises</h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage inspirational scriptures and generate QR codes for church inspiration
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <FaPlus /> Add Scripture
              </button>
              <button
                onClick={downloadQRCode}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <FaDownload /> Download QR
              </button>
            </div>
          </div>
        </div>

        {/* QR Code Section */}
        <div className={`mb-8 p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                <FaQrcode /> Inspiration QR Code
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Download and print this QR code to place at the church entrance. 
                When scanned, it will take visitors to a beautiful inspiration page with random scriptures.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                URL: {window.location.origin}/inspiration
              </div>
            </div>
            <div className="flex-shrink-0">
              {qrCodeUrl && (
                <div ref={qrCanvasRef} className="w-48 h-48 border-2 border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-white">
                  <QRCodeCanvas 
                    value={qrCodeUrl}
                    size={180}
                    level="M"
                    includeMargin={true}
                    
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search scriptures..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300'
              }`}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300'
              }`}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button
              onClick={resetFilters}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
              <FaFilter /> Clear
            </button>
          </div>
        </div>

        {/* Add/Edit Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">
                    {editingPromise ? 'Edit Scripture' : 'Add New Scripture'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingPromise(null);
                      setFormData({
                        title: '',
                        scripture_text: '',
                        reference: '',
                        book: '',
                        chapter: '',
                        verse_start: '',
                        verse_end: '',
                        category: 'general'
                      });
                    }}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    ✕
                  </button>
                </div>

                {/* Online Fetch Section */}
                <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <FaGlobe /> Fetch from Online (KJV)
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g., John 3:16, Psalm 23:1-6"
                      value={onlineReference}
                      onChange={(e) => setOnlineReference(e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDarkMode 
                          ? 'bg-gray-600 border-gray-500 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                    />
                    <button
                      onClick={fetchOnlineScripture}
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <FaRandom /> Fetch
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title *</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Reference *</label>
                      <input
                        type="text"
                        name="reference"
                        value={formData.reference}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Scripture Text *</label>
                    <textarea
                      name="scripture_text"
                      value={formData.scripture_text}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDarkMode 
                          ? 'bg-gray-600 border-gray-500 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Book *</label>
                      <input
                        type="text"
                        name="book"
                        value={formData.book}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Chapter *</label>
                      <input
                        type="number"
                        name="chapter"
                        value={formData.chapter}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Verse Start *</label>
                      <input
                        type="number"
                        name="verse_start"
                        value={formData.verse_start}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Verse End</label>
                      <input
                        type="number"
                        name="verse_end"
                        value={formData.verse_end}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isDarkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDarkMode 
                          ? 'bg-gray-600 border-gray-500 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                    >
                      <option value="general">General</option>
                      <option value="encouragement">Encouragement</option>
                      <option value="faith">Faith</option>
                      <option value="hope">Hope</option>
                      <option value="love">Love</option>
                      <option value="peace">Peace</option>
                      <option value="strength">Strength</option>
                      <option value="healing">Healing</option>
                    </select>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingPromise(null);
                        setFormData({
                          title: '',
                          scripture_text: '',
                          reference: '',
                          book: '',
                          chapter: '',
                          verse_start: '',
                          verse_end: '',
                          category: 'general'
                        });
                      }}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      {loading ? 'Saving...' : editingPromise ? 'Update' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Scriptures Table */}
        <div className={`rounded-lg shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    SN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Scripture
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : promises.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No scriptures found
                    </td>
                  </tr>
                ) : (
                  promises.map((promise,i) => (
                    <tr key={promise.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {i + 1}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-green-900 dark:text-white">
                            {promise.title}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                            {promise.scripture_text.substring(0, 100)}...
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-green-900 dark:text-white">
                        {promise.reference}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          promise.category === 'general' 
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {promise.category.charAt(0).toUpperCase() + promise.category.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          promise.is_active 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {promise.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(promise)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(promise.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className={`px-6 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScripturePromises;
